import { c as _c } from "react/compiler-runtime";
import { useQuery, useMutation } from '@tanstack/react-query';
import { QUERY_KEYS } from 'src/constants/queries';
import { ROUTES } from 'src/constants/routes';
import { Adapter, Integration } from 'src/api/responses';
import { UpdateConnectionRequestPayload } from 'src/api/requests';
import { Integrations } from 'src/api/Integrations';
import useApi from './useApi';
import useAuth from './useAuth';
import useTeams from './useTeams';
import { useRouter } from 'next/navigation';
import useAppStorage from './useAppStorage';
import useRouteWithParams from './useRouteWithParams';
import posthog from 'posthog-js';

/** OAuth `error` values meaning the user backed out rather than the authorization failing. */
const OAUTH_CANCEL_ERRORS = ['user_cancelled_login', 'user_cancelled_authorize', 'access_denied'];
export const useTeamIntegrations = teamId => {
  const $ = _c(13);
  const integrationsClient = useApi(Integrations);
  const {
    isAuthenticated
  } = useAuth();
  let t0;
  if ($[0] !== teamId) {
    t0 = [QUERY_KEYS.INTEGRATIONS.INTEGRATIONS, teamId];
    $[0] = teamId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== integrationsClient || $[3] !== teamId) {
    t1 = async () => {
      const response = await integrationsClient.getIntegrations(teamId);
      const {
        data
      } = response as {
        data: Integration[] | null;
      };
      if (!data) {
        throw new Error("Invalid integrations response format");
      }
      const groupedData = data.reduce(_temp, {});
      return groupedData;
    };
    $[2] = integrationsClient;
    $[3] = teamId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = !!teamId && !!isAuthenticated;
  let t3;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: 900000
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const {
    data: integrations,
    isLoading: isLoadingIntegrations,
    refetch: fetchIntegrations
  } = useQuery(t3);
  let t4;
  if ($[9] !== fetchIntegrations || $[10] !== integrations || $[11] !== isLoadingIntegrations) {
    t4 = {
      integrations,
      isLoadingIntegrations,
      fetchIntegrations
    };
    $[9] = fetchIntegrations;
    $[10] = integrations;
    $[11] = isLoadingIntegrations;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
};
const useIntegrations = () => {
  const integrationsClient = useApi(Integrations);
  const {
    isAuthenticated
  } = useAuth();
  const {
    currentTeam
  } = useTeams();
  const {
    getItem,
    setItem,
    removeItem
  } = useAppStorage();
  const router = useRouter();
  const {
    updatePathWithParams
  } = useRouteWithParams();
  const teamId = currentTeam?.id || '';
  const {
    integrations,
    isLoadingIntegrations,
    fetchIntegrations
  } = useTeamIntegrations(teamId);
  const {
    data: adaptersData,
    isLoading: isLoadingAdapters,
    refetch: fetchAdapters
  } = useQuery({
    queryKey: [QUERY_KEYS.INTEGRATIONS.ADAPTERS, teamId],
    queryFn: async () => {
      const response = await integrationsClient.getAdapters(teamId);
      const {
        data
      } = response;
      if (!data) {
        throw new Error('Invalid adapters response format');
      }
      return data as Adapter[];
    },
    enabled: !!isAuthenticated && !!teamId,
    staleTime: 1000 * 60 * 15 // 15 minutes
  });
  const {
    mutateAsync: createConnectionRequest,
    isPending: createConnectionRequestPending
  } = useMutation({
    mutationFn: async (data_0: {
      teamId: string;
      adapter: string;
      method: string;
    }) => {
      const response_0 = await integrationsClient.createConnectionRequest(data_0.teamId, {
        adapter: data_0.adapter,
        method: data_0.method
      });
      if (response_0.ok && response_0.data) {
        posthog.capture('connect_data_source_attempt', {
          source: data_0.adapter,
          connectionRequestId: response_0.data.id,
          external: data_0.method === 'link'
        });
      }
      return response_0;
    }
  });
  const {
    mutateAsync: createConnectionAttempt,
    isPending: createConnectionAttemptPending
  } = useMutation({
    mutationFn: async (connectionRequestId: string) => {
      const response_1 = await integrationsClient.createConnectionAttempt(connectionRequestId);
      return response_1;
    }
  });
  const {
    mutateAsync: updateIntegration,
    isPending: updateIntegrationPending
  } = useMutation({
    mutationFn: async (data_1: {
      teamId: string;
      adapter: string;
      method: string;
      integration: string;
    }) => {
      const response_2 = await integrationsClient.updateIntegration(data_1.teamId, {
        adapter: data_1.adapter,
        method: data_1.method,
        integration: data_1.integration
      });
      return response_2;
    }
  });
  const {
    mutateAsync: deleteIntegration,
    isPending: deleteIntegrationPending
  } = useMutation({
    mutationFn: async (connectionId: string) => {
      const response_3 = await integrationsClient.deleteIntegration(connectionId);
      if (response_3.ok) {
        fetchIntegrations();
      }
      return response_3;
    }
  });
  const {
    mutateAsync: updateConnectionAttempt,
    isPending: updateConnectionAttemptPending
  } = useMutation({
    mutationFn: async ({
      connectionAttemptId,
      ...payload
    }: UpdateConnectionRequestPayload & {
      connectionAttemptId: string;
    }) => {
      const response_4 = await integrationsClient.updateConnectionAttempt(connectionAttemptId, payload);
      return response_4;
    }
  });
  const handleConnectionAttemptLanding = async (code: string, connectionAttemptId_0: string, callbackParams: Record<string, string>) => {
    if (connectionAttemptId_0 && code) {
      const createConnectionRoute = updatePathWithParams(ROUTES.CREATE_CONNECTION);
      const connectionsRoute = updatePathWithParams(ROUTES.CONNECTIONS);
      const currentStoredConnectionAttempt = JSON.parse(getItem('currentConnectionAttempt') || '{}');
      const external = currentStoredConnectionAttempt.external === true;
      const onboarding = currentStoredConnectionAttempt.onboarding === true;
      const storedConnectionAttemptId = currentStoredConnectionAttempt.connectionAttemptId || '';
      const companyId = currentStoredConnectionAttempt.companyId || '';
      const selectedAccountIdentifier = currentStoredConnectionAttempt.selectedAccountIdentifier || '';
      const connectionRequestId_0 = currentStoredConnectionAttempt.connectionRequestId || '';
      const adapterName = currentStoredConnectionAttempt.adapterName || '';
      if (connectionAttemptId_0 !== storedConnectionAttemptId) {
        console.warn('Connection attempt ID does not match stored ID.');
        return;
      }
      const documentId = currentStoredConnectionAttempt.adapterDocId || '';
      try {
        const response_5 = await updateConnectionAttempt({
          connectionAttemptId: connectionAttemptId_0,
          credentials: code,
          // Some providers sign the whole callback (Shopify), so the backend needs every parameter
          callback_params: callbackParams
        });
        if (response_5.ok && response_5.data) {
          if (response_5.data.status === 'done') {
            removeItem('currentConnectionAttempt');
            removeItem('currentConnectionAttemptData');
            posthog.capture('connected_data_source', {
              source: adapterName,
              connectionRequestId: connectionRequestId_0,
              external
            });
            if (external) {
              router.push(createConnectionRoute + `?step=4&adapterDocId=${documentId}&external=true`);
            } else if (!onboarding) {
              router.push(updatePathWithParams(ROUTES.CONNECTIONS, {
                companyId
              }) + '?success=true&adapter_name=' + encodeURIComponent(adapterName));
            } else {
              router.push(updatePathWithParams(ROUTES.ONBOARDING_CONNECTIONS, {
                companyId
              }) + '?success=true&adapter_name=' + encodeURIComponent(adapterName));
            }
            return;
          }
          const requestId = response_5.data.requestId;
          removeItem('currentConnectionAttempt');
          setItem('currentConnectionAttemptData', response_5.data);
          let route_0 = createConnectionRoute + `?step=3&connectionRequestId=${requestId}&adapterDocId=${documentId}`;
          if (external) {
            route_0 += '&external=true';
          }
          if (onboarding) {
            route_0 += '&onboarding=true';
          }
          if (selectedAccountIdentifier) {
            route_0 += `&selectedAccountIdentifier=${selectedAccountIdentifier}`;
          }
          router.push(route_0);
        } else {
          let reason = 'Unknown';
          if (!response_5.ok && response_5.error.error?.message) {
            reason = response_5.error.error.message;
          }
          posthog.capture('connection_attempt_failed', {
            source: adapterName,
            connectionRequestId: connectionRequestId_0,
            external,
            reason,
            error: response_5.error,
            companyId
          });
          const route_1 = connectionsRoute + `?failure_reason=${encodeURIComponent(reason)}&adapter_name=${encodeURIComponent(adapterName)}`;
          router.push(route_1);
        }
      } catch (error) {
        posthog.capture('connection_attempt_failed', {
          source: adapterName,
          connectionRequestId: connectionRequestId_0,
          external,
          reason: 'Unknown',
          error: error,
          companyId
        });
        const route = connectionsRoute + `?failure_reason=Unknown&adapter_name=${encodeURIComponent(adapterName)}`;
        router.push(route);
      }
    }
  };

  // The provider redirects back with `error` instead of `code` when the user cancels or the
  // authorization fails, so the attempt never reaches the backend.
  const handleConnectionAttemptError = (error_0: string, connectionAttemptId_1: string) => {
    const stored = JSON.parse(getItem('currentConnectionAttempt') || '{}');
    removeItem('currentConnectionAttempt');
    removeItem('currentConnectionAttemptData');
    if (connectionAttemptId_1 !== stored.connectionAttemptId) {
      router.push(updatePathWithParams(ROUTES.CONNECTIONS, {
        companyId: teamId
      }));
      return;
    }
    const external_0 = stored.external === true;
    const onboarding_0 = stored.onboarding === true;
    const companyId_0 = stored.companyId || teamId;
    const adapterName_0 = stored.adapterName || '';
    const connectionsRoute_0 = onboarding_0 ? updatePathWithParams(ROUTES.ONBOARDING_CONNECTIONS, {
      companyId: companyId_0
    }) : updatePathWithParams(ROUTES.CONNECTIONS, {
      companyId: companyId_0
    });
    posthog.capture('connection_attempt_failed', {
      source: adapterName_0,
      connectionRequestId: stored.connectionRequestId || '',
      external: external_0,
      reason: error_0,
      companyId: companyId_0
    });
    if (!OAUTH_CANCEL_ERRORS.includes(error_0)) {
      router.push(connectionsRoute_0 + `?failure_reason=${encodeURIComponent(error_0)}&adapter_name=${encodeURIComponent(adapterName_0)}`);
      return;
    }
    if (external_0) {
      const signInParams = new URLSearchParams({
        step: '1',
        connectionRequestId: stored.connectionRequestId || '',
        adapterDocId: stored.adapterDocId || '',
        external: 'true'
      });
      if (stored.selectedAccountIdentifier) {
        signInParams.set('selectedAccountIdentifier', stored.selectedAccountIdentifier);
      }
      router.push(updatePathWithParams(ROUTES.CREATE_CONNECTION) + '?' + signInParams.toString());
      return;
    }
    router.push(connectionsRoute_0);
  };
  return {
    fetchAdapters,
    fetchIntegrations,
    adapters: adaptersData ?? [],
    integrations,
    isLoadingIntegrations,
    isLoadingAdapters,
    createConnectionRequest,
    createConnectionRequestPending,
    createConnectionAttempt,
    createConnectionAttemptPending,
    updateConnectionAttempt,
    updateConnectionAttemptPending,
    handleConnectionAttemptLanding,
    handleConnectionAttemptError,
    updateIntegration,
    updateIntegrationPending,
    deleteIntegration,
    deleteIntegrationPending
  };
};
export default useIntegrations;
function _temp(acc, integration) {
  if (!acc[integration.adapter]) {
    acc[integration.adapter] = [];
  }
  acc[integration.adapter].push(integration);
  return acc;
}